import React, { Component } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Collapse from '@material-ui/core/Collapse';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableContainer from '@material-ui/core/TableContainer';
import Button from '../../../components/button';

const SECTION_OBJECTS = 'objects';
const EXCLUDED_OBJECT_TYPES = new Set(['TRIGGER']);

class ImportMissingDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedObjects: {},
      searchObjects: '',
      expandedSections: {
        [SECTION_OBJECTS]: true,
      },
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({
        selectedObjects: {},
        searchObjects: '',
        expandedSections: { [SECTION_OBJECTS]: true },
      });
    }
  }

  objectKey(obj) {
    return `${obj.schemaName}::${obj.objectName}::${obj.objectType}`;
  }

  getVisibleMissingObjects() {
    return ((this.props.missingItems && this.props.missingItems.missingObjects) || [])
      .filter((obj) => !EXCLUDED_OBJECT_TYPES.has(obj.objectType));
  }

  getSelectedObjects() {
    const allObjects = this.getVisibleMissingObjects();
    return allObjects.filter((obj) => this.state.selectedObjects[this.objectKey(obj)]);
  }

  filteredObjects() {
    const items = this.getVisibleMissingObjects();
    const q = this.state.searchObjects.trim().toLowerCase();
    if (!q) return items;
    return items.filter((obj) => {
      return (
        obj.objectName.toLowerCase().includes(q) ||
        obj.schemaName.toLowerCase().includes(q) ||
        obj.objectType.toLowerCase().includes(q)
      );
    });
  }

  selectedObjectCount() {
    return Object.values(this.state.selectedObjects).filter(Boolean).length;
  }

  totalSelected() {
    return this.selectedObjectCount();
  }

  toggleObject(obj) {
    const key = this.objectKey(obj);
    this.setState((prev) => ({
      selectedObjects: { ...prev.selectedObjects, [key]: !prev.selectedObjects[key] },
    }));
  }

  selectAllObjects(items, checked) {
    const update = {};
    items.forEach((obj) => {
      update[this.objectKey(obj)] = checked;
    });
    this.setState((prev) => ({ selectedObjects: { ...prev.selectedObjects, ...update } }));
  }

  allObjectsSelected(items) {
    return items.length > 0 && items.every((obj) => this.state.selectedObjects[this.objectKey(obj)]);
  }

  someObjectsSelected(items) {
    return items.some((obj) => this.state.selectedObjects[this.objectKey(obj)]) && !this.allObjectsSelected(items);
  }

  toggleSection(section) {
    this.setState((prev) => ({
      expandedSections: {
        ...prev.expandedSections,
        [section]: prev.expandedSections[section] === false,
      },
    }));
  }

  handleImport = () => {
    const onImport = this.props.onImport;
    if (!onImport) return;

    const objects = this.getSelectedObjects();
    onImport({ objects });
  };

  renderSectionHeader(title, section, count, selectedCount) {
    const expanded = this.state.expandedSections[section] !== false;
    return (
      <div
        className="schema-group-header import-missing-section-header"
        onClick={() => this.toggleSection(section)}
      >
        <span
          className="material-icons"
          style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
        >
          chevron_right
        </span>
        <h4>{title}</h4>
        <Chip label={`${count} found`} size="small" className="import-missing-count-chip import-missing-count-chip-found" />
        {selectedCount > 0 ? (
          <Chip label={`${selectedCount} selected`} size="small" className="import-missing-count-chip import-missing-count-chip-selected" />
        ) : null}
      </div>
    );
  }

  renderObjectsSection() {
    const items = this.filteredObjects();
    const allItems = this.getVisibleMissingObjects();
    const expanded = this.state.expandedSections[SECTION_OBJECTS];
    const selectedCount = this.selectedObjectCount();

    return (
      <div className="schema-group import-missing-section">
        {this.renderSectionHeader('Missing Objects', SECTION_OBJECTS, allItems.length, selectedCount)}
        <Collapse in={expanded}>
          <div className="schema-items import-missing-section-body">
          <TextField
            placeholder="Search objects…"
            value={this.state.searchObjects}
            onChange={(e) => this.setState({ searchObjects: e.target.value })}
            fullWidth
            className="import-missing-search"
            inputProps={{'data-testid': 'dd-import-search'}}
          />
          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary" className="import-missing-empty-state">
              {allItems.length === 0 ? 'No missing objects found — your dictionary is up to date.' : 'No objects match your search.'}
            </Typography>
          ) : (
            <TableContainer className="import-missing-table-container">
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={this.allObjectsSelected(items)}
                        indeterminate={this.someObjectsSelected(items)}
                        onChange={(e) => this.selectAllObjects(items, e.target.checked)}
                        color="primary"
                        inputProps={{'data-testid': 'dd-import-select-all'}}
                      />
                    </TableCell>
                    <TableCell>Object Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Schema</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((obj) => {
                    const key = this.objectKey(obj);
                    const checked = !!this.state.selectedObjects[key];
                    return (
                      <TableRow
                        key={key}
                        hover
                        className={checked ? 'import-missing-row-selected' : ''}
                        onClick={() => this.toggleObject(obj)}
                        data-testid="dd-import-object-row"
                        data-object-name={obj.objectName}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={checked} color="primary" onChange={() => this.toggleObject(obj)} onClick={(e) => e.stopPropagation()} inputProps={{'data-testid': 'dd-import-object-checkbox'}} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">{obj.objectName}</Typography>
                        </TableCell>
                        <TableCell>{obj.objectType}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">{obj.schemaName}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          </div>
        </Collapse>
      </div>
    );
  }

  render() {
    const { open, onClose, isLoadingMissing, isImportingMissing, missingItems } = this.props;
    const total = this.totalSelected();
    const hasAnyObjects = this.getVisibleMissingObjects().length > 0;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="import-missing-dialog" PaperProps={{'data-testid': 'dd-import-missing-dialog'}}>
        <DialogTitle>Import Missing Objects</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="textSecondary" className="import-missing-description">
            Select objects to import from the source database. All columns and attributes for each selected object will be imported automatically.
            No approval workflow is required.
          </Typography>

          {isLoadingMissing && !missingItems ? (
            <div className="import-missing-loading-state">
              <CircularProgress size={36} />
              <Typography variant="body2" color="textSecondary">Connecting to source database…</Typography>
            </div>
          ) : !missingItems ? (
            <Typography variant="body2" color="textSecondary" className="import-missing-empty-state">
              Click "Load Missing" to compare the live source database against your imported dictionary.
            </Typography>
          ) : (
            <>
              {this.renderObjectsSection()}
              {!hasAnyObjects ? (
                <div className="import-missing-empty-results" data-testid="dd-import-missing-empty">
                  <span className="material-icons">check_circle</span>
                  <Typography variant="body1">
                    No missing objects found — your dictionary is up to date.
                  </Typography>
                </div>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions className="import-missing-actions">
          <div>
            {total > 0 ? (
              <Typography variant="body2" color="primary">
                {total} object{total !== 1 ? 's' : ''} selected
              </Typography>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button label="Cancel" className="btn btn-default" onClick={onClose} disabled={isImportingMissing} data-testid="dd-import-cancel" />
            <Button
              label={isImportingMissing ? 'Importing…' : `Import Objects${total > 0 ? ` (${total})` : ''}`}
              className="btn btn-action"
              onClick={this.handleImport}
              disabled={total === 0 || isImportingMissing || isLoadingMissing}
              data-testid="dd-import-confirm"
            />
          </div>
        </DialogActions>
      </Dialog>
    );
  }
}

export default ImportMissingDialog;
